import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import 'echarts-wordcloud';
import * as echarts from 'echarts/core';
import { EChartsType } from 'echarts/core';
import {
  DatasetComponent,
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components';
import { BarChart, EffectScatterChart, LineChart, PieChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { BasicChartProps, ChartRef } from './_config/_interface';
import { patchEchartsTooltipLifecycle } from './utils/patchEchartsTooltipLifecycle';
import './index.scss';

patchEchartsTooltipLifecycle();

echarts.use([
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  GraphicComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  LineChart,
  BarChart,
  PieChart,
  EffectScatterChart,
  UniversalTransition,
  CanvasRenderer,
]);

const ChartInner: React.ForwardRefRenderFunction<ChartRef, BasicChartProps> = (
  { option, notMerge = false, onEvents },
  ref: ForwardedRef<ChartRef>
) => {
  const cRef = useRef<HTMLDivElement>(null);
  const cInstance = useRef<EChartsType | undefined | null>(null);

  const resize = useCallback(() => {
    cInstance.current?.resize({
      animation: { duration: 300 },
    });
  }, []);

  const eventHandlerRef = useRef<Record<string, (event: unknown) => void>>({});
  // 初始化注册组件，监听 cRef 和 option 变化
  useEffect(() => {
    // 减少 不必要渲染
    if (cRef.current) {
      // 校验 Dom 节点上是否已经挂载了 ECharts 实例，只有未挂载时才初始化
      cInstance.current = echarts.getInstanceByDom(cRef.current);
      if (!cInstance.current) {
        cInstance.current = echarts.init(cRef.current, undefined, {
          renderer: 'canvas',
        });
      }
      // setOption 前隐藏 tooltip，避免与 ECharts 内部延迟 show 回调竞态
      if (option && cInstance.current && !cInstance.current.isDisposed()) {
        // hideTip 为尽力而为：实例在 isDisposed 检查与 dispatch 之间被卸载、或
        // tooltip 组件尚未就绪时，dispatchAction 可能抛错；不应阻断后续 setOption。
        // 竞态兜底仍由 patchEchartsTooltipLifecycle 的 setContent 防护承担。
        try {
          cInstance.current.dispatchAction({ type: 'hideTip' });
        } catch {
          // 忽略：主路径是 setOption，hideTip 失败不中断图表更新
        }
        cInstance.current.setOption(option, { notMerge });
      }
      // setOption 后按当前容器尺寸同步一次，避免栅格/侧栏变化时 canvas 仍沿用旧宽度
      requestAnimationFrame(() => {
        cInstance.current?.resize({ animation: { duration: 0 } });
      });
    }
  }, [cRef, notMerge, option]);

  // 监听窗口大小变化重绘
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (cInstance.current) {
        try {
          // 同上：卸载竞态下 hideTip 可能失败，仍需继续 dispose 释放实例
          cInstance.current.dispatchAction({ type: 'hideTip' });
        } catch {
          // 忽略：主路径是 dispose，hideTip 失败不阻止实例销毁
        }
        cInstance.current.dispose();
        cInstance.current = null;
      }
    };
  }, [resize]);

  // 容器尺寸变化（非 window resize，如仪表盘栅格列宽）时重绘
  useEffect(() => {
    const el = cRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(() => {
      resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [resize]);

  // 监听事件
  useEffect(() => {
    if (!cInstance?.current || !onEvents) return;
    if (cInstance?.current && onEvents) {
      Object.keys(onEvents).forEach(t => {
        if (!cInstance?.current) return;
        const handler = (event: unknown) => onEvents[t]?.(event, cInstance.current as EChartsType);
        eventHandlerRef.current[t] = handler;
        cInstance?.current.on(t, handler);
      });
    }
    return () => {
      if (cInstance?.current && onEvents) {
        Object.keys(onEvents).forEach(t => {
          if (!cInstance?.current) return;
          const handler = eventHandlerRef.current[t];
          if (handler) {
            cInstance?.current.off(t, handler);
          }
        });
      }
      eventHandlerRef.current = {};
    };
  }, [onEvents]);

  // 获取实例
  const instance = () => cInstance.current;

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
    cRef
  }));


  return (
    <div ref={cRef} style={{ width: '100%', height: '100%' }} />
  );
};

const Chart = React.forwardRef(ChartInner);

export default Chart;
